import React from 'react'
import styled from '@emotion/styled'
import {space, color} from 'styled-system'

import {Box, Flex, Grid} from 'src/components/Box'
import {
  Body,
  ExtraSmallBody,
  Header2,
  Header4,
  SmallBody,
} from 'src/components/text'
import {BlueUnderlineRounded} from 'src/components/emphasis'
import {theme} from 'src/styles'
import {
  ArrowIconToRight,
  BoxIcon,
  ClockIcon,
  DollarIcon,
} from 'src/components/icons'
import {UnorderedList} from 'src/components/List'

const CardContainer = styled.div`
  height: auto;
  background-color: #ffffff;
  border-radius: 4px;
  overflow: hidden;
  border-radius: 15px;
  box-shadow: 0px 4px 20px 0px #14242c40;
  padding: ${theme.space[4]};
  position: relative;

  ${space}
  ${color}
`

type ScholarshipCardProps = {
  title: string
  description?: string
  source: string
  price?: string
  count?: string
  date: string
  applyLink?: string
  requirements?: string[]
  featuredImageUrl?: string
}

const SearchResultCard = ({
  title,
  description,
  requirements,
  price,
  source,
  count,
  date,
  applyLink = '#',
  featuredImageUrl,
}: ScholarshipCardProps) => {
  return (
    <>
      <Box width="100%" display={{md: 'block', default: 'none'}}>
        <DesktopCard
          title={title}
          description={description}
          requirements={requirements}
          source={source}
          price={price}
          date={date}
          count={count}
          applyLink={applyLink}
          featuredImageUrl={featuredImageUrl}
        />
      </Box>
      <Box display={{md: 'none', default: 'block'}}>
        <MobileCard
          title={title}
          description={description}
          requirements={requirements}
          source={source}
          price={price}
          date={date}
          count={count}
          applyLink={applyLink}
          featuredImageUrl={featuredImageUrl}
        />
      </Box>
    </>
  )
}

const DesktopCard = ({
  title,
  description,
  requirements,
  price,
  source,
  count,
  date,
  applyLink = '#',
  featuredImageUrl,
}: ScholarshipCardProps) => {
  const backgroundImage = featuredImageUrl
    ? {
        backgroundRepeat: 'repeat',
        backgroundImage: `url("${featuredImageUrl}")`,
        height: 270,
        backgroundSize: 'contain',
      }
    : {}
  return (
    <>
      {featuredImageUrl && (
        <a href={applyLink} target="_blank" rel="noreferrer">
          <CardContainer
            css={{
              maxWidth: 840,
              paddingLeft: 50,
              marginTop: 50,
              marginLeft: 'auto',
              ...backgroundImage,
            }}
          ></CardContainer>
        </a>
      )}
      {!featuredImageUrl && (
        <CardContainer
          css={{
            maxWidth: 840,
            paddingLeft: 50,
            marginTop: 50,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <Header2 ml={5} mb={3}>
            {title}
          </Header2>
          <Flex
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            gridGap="2"
            css={{
              paddingRight: 90,
            }}
          >
            <Flex flexDirection="row" gridGap={8}>
              <Box flexBasis={{lg: 415, default: 600}}>
                <Flex flexDirection="column">
                  <Grid
                    gridRowGap="4"
                    gridTemplateColumns={{_: 'auto auto'}}
                    gridTemplateRows={{_: 'auto auto'}}
                  >
                    {(price || count) && (
                      <>
                        <DollarIcon mt="3px" />
                        <Box>
                          {price && <Body fontWeight="700">${price}</Body>}
                          {count && (
                            <Body>{`${count} ${
                              parseInt(count) <= 1 ? 'Award' : 'Awards'
                            }`}</Body>
                          )}
                        </Box>
                      </>
                    )}
                    {date && (
                      <>
                        <ClockIcon mr={1} mt="4px" />
                        <Body fontWeight="700">{date}</Body>
                      </>
                    )}
                    {source && (
                      <>
                        <BoxIcon mt="5px" />
                        <Body fontWeight="700">{source}</Body>
                      </>
                    )}
                  </Grid>
                </Flex>
              </Box>
              <Box>
                <SmallBody>{description}</SmallBody>
                {requirements && (
                  <>
                    <SmallBody mt={3}>Requirements:</SmallBody>
                    <UnorderedList ml={5} gap={0}>
                      {requirements.map((requirement: string, index) => (
                        <SmallBody key={index}>{requirement}</SmallBody>
                      ))}
                    </UnorderedList>
                  </>
                )}
              </Box>
            </Flex>
          </Flex>
          <Flex>
            <a
              target="_blank"
              href={applyLink}
              css={{
                fontWeight: 700,
                width: '100%',
                maxWidth: 'fit-content',
                marginLeft: 'auto',
                marginRight: '20px',
                textDecoration: 'none',
                color: '#000000',
                marginTop: '10px',
              }}
              rel="noreferrer"
            >
              <BlueUnderlineRounded
                css={{
                  borderImageWidth: '0 18px 9px 14px',
                }}
              >
                Apply Now
              </BlueUnderlineRounded>
              <ArrowIconToRight ml={1} />
            </a>
          </Flex>
        </CardContainer>
      )}
    </>
  )
}

const MobileCard = ({
  title,
  price,
  source,
  date,
  applyLink = '#',
  featuredImageUrl,
}: ScholarshipCardProps) => {
  const backgroundImage = featuredImageUrl
    ? {
        backgroundRepeat: 'repeat',
        backgroundImage: `url("${featuredImageUrl}")`,
        height: 100,
        backgroundSize: 'contain',
      }
    : {}
  return (
    <>
      {featuredImageUrl && (
        <a href={applyLink} target="_blank" rel="noreferrer">
          <CardContainer css={{width: 275, ...backgroundImage}}></CardContainer>
        </a>
      )}
      {!featuredImageUrl && (
        <CardContainer css={{width: 275}}>
          <Header4 textAlign="center" fontWeight={700} mb={3}>
            {title}
          </Header4>
          <Flex alignItems="center" justifyContent="space-between">
            <Flex flexDirection="column">
              <Grid
                gridRowGap="1"
                gridTemplateColumns={{_: 'auto auto'}}
                gridTemplateRows={{_: 'auto auto'}}
              >
                {price && (
                  <>
                    <DollarIcon height={20} mt="3px" />
                    <SmallBody fontWeight="700">${price}</SmallBody>
                  </>
                )}
                {date && (
                  <>
                    <ClockIcon height={12} mt="4px" />
                    <ExtraSmallBody>{date}</ExtraSmallBody>
                  </>
                )}
                {source && (
                  <>
                    <BoxIcon height={12} mt="5px" />
                    <ExtraSmallBody>{source}</ExtraSmallBody>
                  </>
                )}
              </Grid>
            </Flex>
            <a
              target="_blank"
              href={applyLink}
              css={{
                fontWeight: 700,
                width: '100%',
                maxWidth: 'fit-content',
                textDecoration: 'none',
                color: '#000000',
                fontSize: '1rem',
                marginBottom: 10,
              }}
              rel="noreferrer"
            >
              <BlueUnderlineRounded
                css={{
                  borderImageWidth: '0 10px 9px 10px',
                }}
              >
                Apply Now
              </BlueUnderlineRounded>
            </a>
          </Flex>
        </CardContainer>
      )}
    </>
  )
}

export default SearchResultCard
